import type {
  ShopifyProductPickFilters,
  SurveyShopifyPickQuestion,
} from "@intelligems/ig-types/schemas/ai/nodes/input-requirement";
import type { UserInputAnswer } from "@intelligems/ig-types/schemas/ai/node-execution";

import { fromGqlId } from "@src/api/formatters/shopify";
import type {
  ProductPickerFilterType,
  ProductPickerState,
} from "@src/store/slices/productPicker/types";

/** The product picker's filters, as its store holds them. */
export type ProductPickerFilters = ProductPickerState["filters"];

const PICKER_FILTER_TYPES: ProductPickerFilterType[] = [
  "product_type",
  "vendor",
  "tag",
  "status",
  "collection_id",
];

/**
 * What a product pick seeds its picker with right now (ENG-7735): the
 * question's static `filters`, plus every `filters_from_steps` binding fed by
 * the merchant's current answer to that earlier step on the card. A catalog
 * pick step answers with the filter's values as-is; an options step goes
 * through the binding's `values` map, so an option that names no catalog
 * value (a decline) seeds nothing. Values are merged, de-duplicated, in order.
 */
export const productPickSeedFilters = (
  question: SurveyShopifyPickQuestion,
  answers: Record<number, UserInputAnswer>
): ShopifyProductPickFilters => {
  const merged: ShopifyProductPickFilters = { ...question.filters };
  for (const binding of question.filters_from_steps ?? []) {
    const choices = answers[binding.question_index]?.choices ?? [];
    const bound = choices.flatMap(({ value }) => {
      if (binding.values === undefined) {
        return [value];
      }
      const catalogValue = binding.values[value];
      return catalogValue === undefined ? [] : [catalogValue];
    });
    if (bound.length > 0) {
      merged[binding.filter] = [
        ...new Set([...(merged[binding.filter] ?? []), ...bound]),
      ];
    }
  }
  return merged;
};

/**
 * A pick's filters as the product picker's store holds them. The picker's
 * Collections filter is keyed by the numeric id, so collection GIDs are cut
 * down to theirs; every other value is the catalog string as-is.
 */
export const toProductPickerFilters = (
  filters: ShopifyProductPickFilters
): ProductPickerFilters => ({
  product_type: filters.product_type ?? [],
  vendor: filters.vendor ?? [],
  tag: filters.tag ?? [],
  status: filters.status ?? [],
  collection_id: (filters.collection_id ?? []).map(fromGqlId),
});

/** Whether two picker filter sets select the same values, in any order. */
export const sameProductPickerFilters = (
  a: ProductPickerFilters,
  b: ProductPickerFilters
): boolean =>
  PICKER_FILTER_TYPES.every((type) => {
    const left = new Set(a[type]);
    const right = new Set(b[type]);
    return (
      left.size === right.size && [...left].every((value) => right.has(value))
    );
  });
